'use client';

import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { QuestionsFilledOut, ContactFilledOut, LeadCreated } from './contact/events';
import { validatePostalCode, validatePhoneNumber } from './contact/validators';
import { enqueue } from '@/lib/marketing/on_interaction/gtm';
import { readSession } from '@/lib/marketing/helpers';
import useGoogleConversion from '@/hooks/useGoogleConversion';
import { useMarketing } from '@/providers/marketing/MarketingContext';
import { readLeadSession } from '@/lib/lead-session';
import { leadInput, LeadInput, PHONE_MIN_LENGTH, maskUSPhone } from '@jlg-website/shared';

const SuccessMessage = dynamic(() => import('@/components/pages/forms/contact/success-message'), {
  ssr: false,
});

const FailureMessage = dynamic(() => import('@/components/pages/forms/contact/failure-message'), {
  ssr: false,
});

type Status = 'succeeded' | 'failed' | 'fetching' | 'hidden';

export function useContact(options?: { hideInjuryQuestions?: boolean }) {
  const hideInjuryQuestions = options?.hideInjuryQuestions ?? false;
  const [status, setStatus] = useState<Status | null>(null);
  const form = useForm<LeadInput>({
    resolver: zodResolver(leadInput),
    defaultValues: {
      session: '',
      form: {
        id: 1,
      },
      campaign: {
        id: 1,
        lead_type: {
          id: 0,
          lead_type_name: 'Personal Injury',
        },
        lead_source: {
          id: 0,
        },
        lead_score_baseline: 0,
        offer: {
          id: 0,
        },
      },
      lead: {
        answers: hideInjuryQuestions
          ? {}
          : {
              '1': { value: [], score: [] },
              '2': { value: [], score: [] },
            },
        contact: {
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          zip_code: '',
          details: '',
          consent: false,
        },
        is_qualified: true,
        is_duplicate: false,
        score: 0,
      },
      is_2fa_enabled: false,
      analytics: {
        ip_address: '',
        user_agent: '',
        url: '',
      },
    },
  });

  const marketingConversion = useGoogleConversion();
  const marketing = useMarketing();

  async function onSubmit(data: LeadInput) {
    const phoneNumber = data.lead.contact.phone;
    const [{ is_valid: isZipValid }, { is_valid: isPhoneValid }] = await Promise.all([
      validatePostalCode(data.lead.contact.zip_code),
      validatePhoneNumber(`+1${phoneNumber}`),
    ]);

    if (!isZipValid) {
      setError('lead.contact.zip_code', { type: 'manual', message: 'Invalid postal code' });
    }

    const isPhoneInvalid = !phoneNumber || phoneNumber.length < PHONE_MIN_LENGTH || !isPhoneValid;
    if (isPhoneInvalid) {
      setError('lead.contact.phone', { type: 'manual', message: 'Invalid phone number' });
    }

    if (!isZipValid || isPhoneInvalid) {
      return;
    }

    setStatus('fetching');
    addEventToGaQueue('form_submit');

    data.campaign.id = marketing.campaignId;
    // `leads.lead_type_id` is a FK to `claim_sections.id`, so the lead row carries the internal
    // id — the external marketing one only ever goes to the analytics events.
    data.campaign.lead_type.id = marketing.leadTypeInternalId;
    data.campaign.lead_source.id = marketing.leadSourceId;
    data.campaign.offer.id = marketing.offerId;
    data.plugins = {
      trusted_form: collectTrustedFormData(),
    };

    try {
      QuestionsFilledOut(data, marketing);
      ContactFilledOut(data, marketing);
    } catch (e) {
      console.error(e);
    }

    const leadSession = readLeadSession();

    try {
      const payload = {
        ...data,
        ...leadSession,
      };

      const response = await fetch('/api/v1/lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const { id } = await response.json();
        LeadCreated(id, payload, marketing);
        // TODO: exporting is taking longer than the page can wait, how to have that exporting status and not having the visitor waiting too long?
        //const isExported = await pollExportStatus(id);
        const isExported = true;
        setStatus(isExported ? 'succeeded' : 'failed');

        // Both conversions describe the same campaign, and the lead type they report is the
        // external marketing one — never `data.campaign.lead_type.id`, the FK to `claim_sections`.
        const conversionCampaign = {
          id: data.campaign.id,
          lead_type_id: marketing.leadTypeId,
          offer_id: marketing.offerId,
          lead_type_name: data.campaign.lead_type.lead_type_name,
          lead_score_baseline: data.campaign.lead_score_baseline,
        };
        const conversionAnswers = { ...data.lead.answers };

        marketingConversion.sendCompleteRegistrationEvent(
          leadSession.session,
          conversionCampaign,
          conversionAnswers
        );

        marketingConversion.sendLeadEvent(
          leadSession.session,
          conversionCampaign,
          conversionAnswers,
          {
            first_name: data.lead.contact.first_name,
            last_name: data.lead.contact.last_name,
            phone: data.lead.contact.phone,
            email: data.lead.contact.email,
            zip_code: data.lead.contact.zip_code,
          }
        );

        return;
      }
      setStatus('failed');
    } catch (e) {
      console.error('Failed to create lead', e);
      setStatus('failed');
    }
  }

  function collectTrustedFormData() {
    const trustedForm = {
      cert_url: '',
      token: '',
    };

    try {
      trustedForm.cert_url =
        (document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement)?.value ??
        '';
      trustedForm.token =
        (document.querySelector('input[name="xxTrustedFormToken"]') as HTMLInputElement)?.value ??
        '';
    } catch (err: unknown) {
      console.error(err);
    }

    return trustedForm;
  }

  const {
    watch,
    handleSubmit,
    register,
    setValue,
    setError,
    getValues,
    trigger,
    control,
    formState: { errors, isSubmitting },
  } = form;

  const formValues = useWatch({ control }) as LeadInput;
  const debouncedLogTimeoutRef = useRef<number | null>(null);
  const eventsEmitted = useRef<string[]>([]);

  function onCaseTypeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setValue('lead.answers.1.value', [value], { shouldValidate: true });
    setValue('lead.answers.1.score', [0], { shouldValidate: true });
  }

  function onWereYouInjuredChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setValue('lead.answers.2.value', [value], { shouldValidate: true });
    setValue('lead.answers.2.score', [0], { shouldValidate: true });
  }

  async function onZipCodeBlur(e: React.FocusEvent<HTMLInputElement>) {
    const postalCode = e.target.value;
    const { is_valid: isValid } = await validatePostalCode(postalCode);

    if (!isValid) {
      setError('lead.contact.zip_code', { type: 'manual', message: 'Invalid postal code' });
      return;
    }

    setValue('lead.contact.zip_code', postalCode, { shouldValidate: true });
  }

  async function onPhoneNumberBlur(e: React.FocusEvent<HTMLInputElement>) {
    const phoneNumber = e.target.value;
    const phoneNumberWithDDI = `+1${phoneNumber}`;

    if (!phoneNumber || phoneNumber.length < PHONE_MIN_LENGTH) {
      setError('lead.contact.phone', { type: 'manual', message: 'Invalid phone number' });
      return;
    }

    const { is_valid: isValid } = await validatePhoneNumber(phoneNumberWithDDI);

    if (!isValid) {
      setError('lead.contact.phone', { type: 'manual', message: 'Invalid phone number' });
      return;
    }

    setValue('lead.contact.phone', phoneNumber, { shouldValidate: true });
  }

  function onPhoneNumberChange(e: React.ChangeEvent<HTMLInputElement>) {
    const phoneNumber = e.target.value;
    const maskedPhoneNumber = maskUSPhone(phoneNumber);
    setValue('lead.contact.phone', maskedPhoneNumber, { shouldValidate: false });
  }

  const addEventToGaQueue = useCallback((eventName: string) => {
    if (!eventsEmitted.current.includes(eventName)) {
      eventsEmitted.current.push(eventName);

      const { sessionId, visitorId } = readSession();

      enqueue({
        event: eventName,
        session_id: sessionId,
        visitor_id: visitorId,
      });
    }
  }, []);

  const emitProgressEventsToGA = useCallback(() => {
    const { lead } = formValues;
    const { contact } = lead;
    // TODO: refactor to check if field is valid
    const formProgress1 = contact.first_name.length > 2 && contact.last_name.length > 2;
    const formProgress2 =
      contact.phone.length > 10 && contact.zip_code.length > 4 && contact.email.length > 5;
    const formProgress3 =
      (lead.answers[1]?.value.length ?? 0) > 0 && (lead.answers[2]?.value.length ?? 0) > 0;
    const formProgress4 = contact?.details ? contact.details.length > 2 : false;

    if (formProgress1) {
      addEventToGaQueue('form_progress_1');
    }
    if (formProgress2) {
      addEventToGaQueue('form_progress_2');
    }
    if (formProgress3) {
      addEventToGaQueue('form_progress_3');
    }
    if (formProgress4) {
      addEventToGaQueue('form_progress_4');
    }
  }, [addEventToGaQueue, formValues]);

  function hideForm() {
    setStatus('hidden');
  }

  useEffect(() => {
    if (debouncedLogTimeoutRef.current !== null) {
      clearTimeout(debouncedLogTimeoutRef.current);
    }

    debouncedLogTimeoutRef.current = window.setTimeout(async () => {
      emitProgressEventsToGA();
    }, 1000);

    return () => {
      if (debouncedLogTimeoutRef.current !== null) {
        clearTimeout(debouncedLogTimeoutRef.current);
        debouncedLogTimeoutRef.current = null;
      }
    };
  }, [emitProgressEventsToGA]);

  const isFetching = status === 'fetching';
  const isFailed = status === 'failed';
  const isSucceeded = status === 'succeeded';
  const isHidden = status === 'hidden';
  const isDisabled = isFetching || isSubmitting;

  return {
    watch,
    handleSubmit,
    register,
    setValue,
    getValues,
    errors,
    isSubmitting,
    onSubmit,
    SuccessMessage,
    FailureMessage,
    isFetching,
    isFailed,
    isSucceeded,
    isDisabled,
    isHidden,
    onWereYouInjuredChange,
    onCaseTypeChange,
    caseTypeValue: formValues.lead.answers[1]?.value,
    wereYouInjuredValue: formValues.lead.answers[2]?.value,
    zipCodeValue: formValues.lead.contact.zip_code,
    phoneNumberValue: formValues.lead.contact.phone,
    consentValue: formValues.lead.contact.consent,
    onZipCodeBlur,
    onPhoneNumberBlur,
    onPhoneNumberChange,
    trigger,
    hideForm,
  };
}

export default useContact;
